import React, { useState } from 'react';

const PlayerManagementScreen = ({ savedPlayers, onUpdatePlayers, onBack, onHome, onLogout }) => {
    const players = savedPlayers || [];
    const [newPlayerName, setNewPlayerName] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingName, setEditingName] = useState('');

    const handleAddPlayer = () => {
        const trimmedName = newPlayerName.trim();
        if (trimmedName && !players.includes(trimmedName)) {
            const updatedPlayers = [...players, trimmedName];
            onUpdatePlayers(updatedPlayers);
            setNewPlayerName('');
        }
    };

    const handleDeletePlayer = (index) => {
        const updatedPlayers = players.filter((_, i) => i !== index);
        onUpdatePlayers(updatedPlayers);
    };

    const handleStartEdit = (index) => {
        setEditingIndex(index);
        setEditingName(players[index]);
    };

    const handleSaveEdit = () => {
        const trimmedName = editingName.trim();
        if (trimmedName && !players.includes(trimmedName)) {
            const updatedPlayers = [...players];
            updatedPlayers[editingIndex] = trimmedName;
            onUpdatePlayers(updatedPlayers);
        }
        setEditingIndex(null);
        setEditingName('');
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditingName('');
    };

    return (
        <div className="h-screen bg-[#1a2332] font-['Inter'] text-[#F8FAFC] flex flex-col">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Header */}
            <header className="relative z-20 flex items-center justify-between px-4 py-3 bg-[#1a2332]/80 backdrop-blur-md border-b border-white/5 shadow-lg">
                <button
                    onClick={onBack}
                    className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                    title="Back"
                >
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400 drop-shadow-sm">
                    MANAGE PLAYERS
                </h1>

                <button
                    onClick={onHome}
                    className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                    title="Home"
                >
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                </button>
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-1 overflow-y-auto p-4 sm:p-6">
                <div className="w-full max-w-2xl mx-auto space-y-6">
                    {/* Add New Player */}
                    <div className="bg-[#1e293b]/50 backdrop-blur-sm border border-white/10 rounded-2xl p-4 sm:p-6">
                        <h2 className="text-lg font-bold text-amber-400 mb-4">Add New Player</h2>
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={newPlayerName}
                                onChange={(e) => setNewPlayerName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddPlayer()}
                                placeholder="Enter player name"
                                className="flex-1 rounded-xl bg-[#2d3e50] border-2 border-[#3d4f61] text-[#F8FAFC] h-12 px-4 text-sm sm:text-base placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            />
                            <button
                                onClick={handleAddPlayer}
                                disabled={!newPlayerName.trim()}
                                className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-xl shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Player List */}
                    <div className="bg-[#1e293b]/50 backdrop-blur-sm border border-white/10 rounded-2xl p-4 sm:p-6">
                        <h2 className="text-lg font-bold text-amber-400 mb-4">
                            Saved Players ({players.length})
                        </h2>

                        {players.length === 0 ? (
                            <div className="text-center py-12">
                                <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <p className="text-gray-500 text-sm">No players saved yet</p>
                                <p className="text-gray-600 text-xs mt-1">Add players above to get started</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {players.map((player, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors"
                                    >
                                        {editingIndex === index ? (
                                            <>
                                                <input
                                                    type="text"
                                                    value={editingName}
                                                    onChange={(e) => setEditingName(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                                                    className="flex-1 rounded-lg bg-[#2d3e50] border border-[#3d4f61] text-[#F8FAFC] h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                    autoFocus
                                                />
                                                <button
                                                    onClick={handleSaveEdit}
                                                    className="p-2 rounded-lg bg-green-600 hover:bg-green-500 transition-colors"
                                                    title="Save"
                                                >
                                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="p-2 rounded-lg bg-gray-600 hover:bg-gray-500 transition-colors"
                                                    title="Cancel"
                                                >
                                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="flex-1 text-white font-medium">{player}</span>
                                                <button
                                                    onClick={() => handleStartEdit(index)}
                                                    className="p-2 rounded-lg bg-white/5 hover:bg-blue-500/20 border border-white/10 hover:border-blue-500/30 transition-colors"
                                                    title="Edit"
                                                >
                                                    <svg className="w-5 h-5 text-gray-400 hover:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePlayer(index)}
                                                    className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 transition-colors"
                                                    title="Delete"
                                                >
                                                    <svg className="w-5 h-5 text-gray-400 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PlayerManagementScreen;
